import { Card, CardContent, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import { MANAGE_PROFILE } from '@constants/routes';

import ManageAccountLayout from '@views/ManageAccount/ManageAccountLayout';
import ChangeAvatar from '@views/ManageAccount/Profile/ChangeAvatar';
import ChangeEmail from '@views/ManageAccount/Profile/ChangeEmail';
import ChangeMfa from '@views/ManageAccount/Profile/ChangeMfa';
import ChangeName from '@views/ManageAccount/Profile/ChangeName';
import ChangePassword from '@views/ManageAccount/Profile/ChangePassword';
import ChangePhone from '@views/ManageAccount/Profile/ChangePhone';

const messages = defineMessages({
  avatar: {
    id: 'manageProfileSetting.avatar',
    defaultMessage: 'Change Profile Picture',
  },
  email: {
    id: 'manageProfileSetting.email',
    defaultMessage: 'Change Email',
  },
  mfa: {
    id: 'manageProfileSetting.mfa',
    defaultMessage: 'Change Multi-Factor Authentication',
  },
  phone: {
    id: 'manageProfileSetting.phone',
    defaultMessage: 'Change Phone Number',
  },
  name: {
    id: 'manageProfileSetting.name',
    defaultMessage: 'Change Name',
  },
  password: {
    id: 'manageProfileSetting.password',
    defaultMessage: 'Change Password',
  },
  fallback: {
    id: 'manageProfileSetting.profileAndSecurity',
    defaultMessage: 'Profile and Security',
  },
  qrCode: {
    id: 'manageProfileSetting.qr-code',
    defaultMessage: 'Retrieve QR Code',
  },
  profileSubtitle: {
    id: 'profileSettings.profileSubtitle',
    defaultMessage: "Display as your account's profile photo.",
  },
});

enum UpdateProfileSettingParam {
  Avatar = 'avatar',
  Email = 'email',
  Mfa = 'mfa',
  Name = 'name',
  Password = 'password',
  Phone = 'phone',
}

export default function ManageProfileSettingsPage(
  props: PagePropsWithBreadcrumbs
) {
  const { formatMessage } = useIntl();
  const setting = props.params.setting;
  const title =
    formatMessage({ id: `manageProfileSetting.${setting}` }) ||
    formatMessage(messages.fallback);

  useEffect(() => {
    if (
      !setting ||
      !Object.values<string>(UpdateProfileSettingParam).includes(setting)
    ) {
      navigate(MANAGE_PROFILE);
    }
  }, [setting]);

  const updatedCrumbs = props.pageContext.breadcrumb.crumbs.map((crumb) => {
    if (crumb.crumbLabel === '[setting]') {
      return { ...crumb, crumbLabel: title, pathname: props.uri };
    }
    return crumb;
  });

  const updatedProps = {
    ...props,
    pageContext: {
      breadcrumb: {
        ...props.pageContext.breadcrumb,
        crumbs: updatedCrumbs,
      },
    },
  };

  return (
    <ManageAccountLayout friendlyName={title} pageProps={updatedProps}>
      {({ loading, refetch, user }) => {
        const getComponent = () => {
          switch (setting) {
            case UpdateProfileSettingParam.Avatar:
              return (
                <>
                  <Typography variant="subtitle2">
                    {formatMessage(messages.profileSubtitle)}
                  </Typography>

                  <ChangeAvatar loading={loading} user={user} />
                </>
              );
            case UpdateProfileSettingParam.Email:
              return <ChangeEmail refetch={refetch} user={user} />;
            case UpdateProfileSettingParam.Mfa:
              return <ChangeMfa />;
            case UpdateProfileSettingParam.Name:
              return (
                <ChangeName
                  initialLoading={loading}
                  refetch={refetch}
                  user={user}
                />
              );
            case UpdateProfileSettingParam.Password:
              return <ChangePassword />;
            case UpdateProfileSettingParam.Phone:
              return <ChangePhone />;
            default:
              return null;
          }
        };
        return (
          <Card>
            <CardContent>
              <ContentLoader loading={loading}>{getComponent()}</ContentLoader>
            </CardContent>
          </Card>
        );
      }}
    </ManageAccountLayout>
  );
}
